import { useCallback, useRef, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";

import DocumentationIconButton from "components/DocumentationIconButton";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Card from "ds/components/Card";
import CardCollapsible from "ds/components/Card/Collapsible";
import FormField from "ds/components/Form/Field";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Select from "ds/components/Select";
import Typography from "ds/components/Typography";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import { useFormValidations } from "hooks/useFormValidations";
import { BlueprintState, TemplateDeployment, TemplateVersion } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import DeploymentForm from "../components/DeploymentForm";
import { buildMergedVersionInputs } from "./helpers";
import { VersionTemplateFormFields } from "../components/DeploymentForm/types";
import useDeploymentForm from "../components/DeploymentForm/useDeploymentForm";
import styles from "./styles.module.css";

type UpdateDeploymentFormProps = {
  version: TemplateVersion;
  availableVersions: TemplateVersion[];
  deployment: TemplateDeployment;
  templateId: string;
  onClose: () => void;
};

const UpdateDeploymentForm = ({
  version,
  availableVersions,
  deployment,
  templateId,
  onClose,
}: UpdateDeploymentFormProps) => {
  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion>(version);

  const isSubmittingSuccessfullyRef = useRef(false);

  const handleSubmitCallback = useCallback(() => {
    isSubmittingSuccessfullyRef.current = true;
    onClose();
  }, [onClose]);

  const {
    builderForm,
    onSubmit,
    loading,
    hasErrors,
    hasPreview,
    errors,
    inputs,
    secretFieldsState,
    isDirty,
    setInputs,
    resetSecretFieldActions,
  } = useDeploymentForm({
    version: selectedVersion,
    deployment,
    templateId,
    submitCallback: handleSubmitCallback,
  });

  useBlockNavigationModal({
    isNavigationBlocked: isDirty,
    skipBlockRef: isSubmittingSuccessfullyRef,
  });

  const handleOpenFullDescription = useCallback(() => {
    if (selectedVersion.instructions) {
      showFullDescription({ description: selectedVersion.instructions });
    }
  }, [selectedVersion.instructions]);

  const { handleSubmit } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const handleUpdate: SubmitHandler<VersionTemplateFormFields> = useCallback(
    async (formData) => {
      const isValid = await runFormValidations();
      if (!isValid) return;

      onSubmit(formData);
    },
    [onSubmit, runFormValidations]
  );

  const handleVersionChange = useCallback(
    (newVersionId: string) => {
      const newVersion = availableVersions.find((v) => v.id === newVersionId);
      if (!newVersion || newVersion.id === selectedVersion.id) return;

      const currentValues = builderForm.getValues();

      const mergedInputs = buildMergedVersionInputs(newVersion.inputs, currentValues.inputs);

      setInputs(newVersion.inputs);
      resetSecretFieldActions();

      // Reset with the deployment's original versionId so RHF's dirty baseline stays
      // at the original version. The setValue below then makes the change detectable as dirty.
      builderForm.reset({
        ...currentValues,
        inputs: mergedInputs,
        versionId: deployment.templateVersion.id,
      });

      if (newVersion.id !== deployment.templateVersion.id) {
        builderForm.setValue("versionId", newVersion.id, { shouldDirty: true });
      }

      setSelectedVersion(newVersion);
    },
    [
      availableVersions,
      builderForm,
      deployment.templateVersion.id,
      resetSecretFieldActions,
      selectedVersion.id,
      setInputs,
    ]
  );

  const versionSelectItems = availableVersions.map((v) => ({
    value: v.id,
    label: v.version ?? v.id,
  }));

  return (
    <FormProvider {...builderForm}>
      <form className={styles.form} onSubmit={handleSubmit(handleUpdate)}>
        <FullScreenModalBody noMaxWidth>
          <Box justify="center" gap="x-large" className={styles.contentWrapper} fullWidth>
            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              align="center"
              className={styles.formSection}
            >
              <Box direction="column" className={styles.formFields} gap="large">
                {availableVersions.length > 1 && (
                  <FormField
                    label="Version"
                    helperText="When changing versions, the form fields may differ."
                    noMargin
                    fullWidth
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={selectedVersion.id}
                        items={versionSelectItems}
                        onChange={handleVersionChange}
                        placeholder="Select a version"
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}

                <Card>
                  <DeploymentForm
                    item={selectedVersion}
                    isPublished={selectedVersion.state === BlueprintState.Published}
                    hasPreview={hasPreview}
                    hasErrors={hasErrors}
                    errors={errors}
                    inputs={inputs}
                    isUpdateMode
                    onOpenFullDescription={handleOpenFullDescription}
                    secretFieldsState={secretFieldsState}
                  />
                </Card>
              </Box>
            </Box>

            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              className={styles.instructionsSection}
            >
              <CardCollapsible
                title="Instructions"
                isCollapsed={isInstructionsCollapsed}
                onToggle={() => setIsInstructionsCollapsed(!isInstructionsCollapsed)}
                ariaLevel={2}
                ariaLabel="Deployment instructions"
              >
                {selectedVersion.instructions ? (
                  <Markdown markup={selectedVersion.instructions} />
                ) : (
                  <Typography tag="div" variant="p-body2" color="secondary">
                    No deployment instructions provided for this template.
                  </Typography>
                )}
              </CardCollapsible>
            </Box>
          </Box>
        </FullScreenModalBody>

        <FullScreenModalFooter justify="between">
          <DocumentationIconButton
            href={getDocsUrl("/concepts/template/deployments")}
            tooltipText="Go to templates documentation"
            size="small"
          />
          <Button variant="primary" type="submit" loading={loading} disabled={loading}>
            Update
          </Button>
        </FullScreenModalFooter>
      </form>
    </FormProvider>
  );
};

export default UpdateDeploymentForm;
